/*
 * subtreeSweep — the one "Expand all / Collapse all" that works on every node type.
 *
 * Why (2026-10-26, Brandon's AT-R2SUU): every menu's Expand all used to carry its own hand-written
 * list of which child keys to open (expandCollapseDescendants in outline.js, plus ~25 bespoke React
 * setAll() handlers). Any node type missing from a list did nothing, and every list stopped at
 * whatever depth its author thought of — threads, RFIs, units/levels, sheets, gates, etc. broke.
 *
 * This reads the DOM instead of knowing the tree: every collapsible outline node is a `.wf-node`
 * whose `data-key` is its `wf.collapsed` key, and whose row triangle shows `.wf-tri-icon--right`
 * when collapsed / `--down` when open. A sweep sets every descendant to the wanted state, lets the
 * outline re-render (lazy sections load their data through their own useEffect), and repeats until
 * nothing new appears — so it reaches as deep as the tree goes, for node types that don't exist yet.
 *
 * Scope: the subtree of the node whose menu was used. A zoom-title menu has no node on screen (the
 * zoomed node IS the page), so its scope is the whole outline.
 */
import { wf } from './state.js'
import { ctx } from './ctx.js'

const CONTAINERS = ['aj-sc-root', 'aj-sc-sections']
const MAX_ROUNDS = 80
// Slow sections (Owner Criteria, sheets) can take 10+ s to load their children; keep looking for
// this long after the last change before stopping, and never longer than SWEEP_MS overall.
const QUIET_MS = 15000
const SWEEP_MS = 60000
let _token = 0

function cssEsc(s) {
  return (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&')
}

/* Keys that name the node the page is currently zoomed into (its title, not a row). */
function isZoomKey(key) {
  if (!key) return true
  return key === wf.zoomSection ||
    (wf.zoomMsId && key === 'ms:' + wf.zoomMsId) ||
    (wf.zoomPhaseId && key === 'ph:' + wf.zoomPhaseId) ||
    (wf.zoomRoot && key === 'root:' + wf.zoomRoot) ||
    (wf.zoomProjectId && key === 'proj:' + wf.zoomProjectId) ||
    (wf.zoomCat && wf.zoomMsId && key === 'cat:' + wf.zoomMsId + ':' + wf.zoomCat) ||
    false
}

/* The scope roots for a key: every on-screen node carrying it, else the whole outline when the key
   is the zoomed node (or none was given). `strict` callers (an explicit key from the engine) get
   null rather than the whole outline when the key is neither on screen nor the zoom. */
function scopeRoots(key, strict) {
  const conts = CONTAINERS.map(id => document.getElementById(id)).filter(Boolean)
  if (key) {
    const sel = '.wf-node[data-key="' + cssEsc(key) + '"]'
    const hits = []
    conts.forEach(c => c.querySelectorAll(sel).forEach(n => hits.push(n)))
    if (hits.length) return { roots: hits, whole: false }
  }
  if (strict && !isZoomKey(key)) return null
  return { roots: conts, whole: true }
}

function isShown(el) {
  return !!(el && el.offsetParent !== null && !el.closest('.wf-hidden-done'))
}

/* Collapsible nodes under the scope whose state differs from the wanted one. */
function pending(roots, expand) {
  const icon = expand ? '.wf-tri-icon--right' : '.wf-tri-icon--down'
  const out = []
  const seen = new Set()
  roots.forEach(root => {
    const list = [];
    // The scope node itself counts (Expand all on a collapsed row opens the row first).
    if (root.classList && root.classList.contains('wf-node')) list.push(root)
    root.querySelectorAll('.wf-node').forEach(n => list.push(n))
    list.forEach(node => {
      if (seen.has(node)) return
      seen.add(node)
      const row = node.firstElementChild
      if (!row || !row.classList || !row.classList.contains('wf-row')) return
      const tri = row.querySelector(':scope > .wf-triangle')
      if (!tri || tri.classList.contains('wf-triangle--hint')) return
      if (!tri.querySelector(icon)) return
      if (!isShown(node)) return
      out.push({ node, tri, key: node.getAttribute('data-key') })
    })
  })
  return out
}

function rerender() {
  if (typeof ctx.render === 'function') {
    wf.suppressAnim = true
    try { ctx.render() } finally { wf.suppressAnim = false }
  } else {
    window.dispatchEvent(new Event('outline-render'))
  }
}

/**
 * sweepSubtree(key, expand) — open or close everything under `key` (see header).
 * Runs over several animation-settled rounds; a newer sweep cancels an older one.
 */
export function sweepSubtree(key, expand, opts) {
  const strict = !!(opts && opts.strict)
  const token = ++_token
  // Expanding the whole home list (every project, every phase, every milestone) would load the
  // entire firm; the home menu keeps its one-level behavior from expandCollapseDescendants.
  if (expand && (key === 'root:projects' || (!key && !wf.zoomProjectId))) return
  const tried = new Map()   // key -> round we set it; still closed a round later = click instead
  const clicked = new WeakSet() // rows whose own triangle we already clicked
  const started = Date.now()
  let lastChange = started
  let round = 0

  function step() {
    if (token !== _token) return
    round++
    const scope = scopeRoots(key, strict)
    if (!scope) return
    const all = pending(scope.roots, expand)
    // Actionable: a key not set yet, or a row still wrong a round after its key was set / no key.
    const ripe = t => !t.key || (tried.has(t.key) && tried.get(t.key) < round)
    const todo = all.filter(t => (t.key && !tried.has(t.key)) || (ripe(t) && !clicked.has(t.node)))
    if (!todo.length) {
      // Lazy sections may still be loading their children — keep looking for a while.
      const now = Date.now()
      if (expand && now - lastChange < QUIET_MS && now - started < SWEEP_MS && round < MAX_ROUNDS) setTimeout(step, 1000)
      return
    }
    lastChange = Date.now()
    let setAny = false
    let clickLater = false
    todo.forEach(t => {
      if (t.key && !tried.has(t.key)) {
        tried.set(t.key, round)
        wf.collapsed[t.key] = !expand
        if (expand && typeof ctx.ensureNodeLoaded === 'function') ctx.ensureNodeLoaded(t.key)
        setAny = true
      } else {
        // No key, or its data-key isn't the collapse key it actually reads — use its own triangle.
        clickLater = true
      }
    })
    if (setAny) rerender()
    // Click fallbacks one at a time against fresh DOM (a click may re-render the outline). Each row
    // is clicked once, so a triangle that ignores clicks can't flap.
    if (clickLater) {
      for (let guard = 0; guard < 200; guard++) {
        const sc = scopeRoots(key, strict)
        const next = sc && pending(sc.roots, expand).find(t => ripe(t) && !clicked.has(t.node))
        if (!next) break
        clicked.add(next.node)
        if (next.tri.isConnected) next.tri.click()
      }
    }
    if (round < MAX_ROUNDS && Date.now() - started < SWEEP_MS) setTimeout(step, 250)
  }
  // The person acting again (a click, a key) ends the sweep — it must never re-open a branch they
  // just closed while it waits on slow sections. Synthetic .click() fires no pointerdown.
  const stop = () => { if (token === _token) _token++ }
  document.addEventListener('pointerdown', stop, { capture: true, once: true })
  document.addEventListener('keydown', stop, { capture: true, once: true })
  setTimeout(() => {
    document.removeEventListener('pointerdown', stop, { capture: true })
    document.removeEventListener('keydown', stop, { capture: true })
  }, SWEEP_MS + 1000)
  // Let the menu's own handler (and its render) land first.
  setTimeout(step, 30)
}

/**
 * The sweep scope for a React action menu opened from `anchorEl`: `{ key }` for a menu on an
 * outline node, `{ key: null }` (whole outline) for the zoom-title menu, or null for a menu that
 * isn't part of the outline at all (a workspace, a modal) — those never sweep.
 */
export function sweepScopeForAnchor(anchorEl) {
  if (!anchorEl || !anchorEl.closest) return null
  const zt = document.getElementById('aj-sc-zoom-title')
  if (zt && zt.contains(anchorEl)) return { key: null }
  const node = anchorEl.closest('.wf-node[data-key]')
  if (!node) return null
  if (!CONTAINERS.some(id => { const c = document.getElementById(id); return c && c.contains(node) })) return null
  return { key: node.getAttribute('data-key') }
}
